import { cloneDeep } from 'lodash'
import { numberOrStringAsNumber } from '../utils/numberOrStringAsNumber'
import { addNotification } from '../addNotification'
import { patchEntityData } from '../saveEntityData'
import { TypeAndProductLevelDescr } from './TypeAndProductLevelDescr'
import { SupplierAndTsdDescr } from './SupplierAndTsdDescr'
import {
  doesQualifyToBeHidden,
  ItemAvailabilityIds,
} from './ItemAvailabilityStatus'
import { getRecursPaidDescr, getRecursMrcAmount } from './RecursPaid'
import { propagateOMSServicesData } from './OpportunityMSService'
import { propagateMOMSServicesData } from './OrderMSService'
import type { TSiloOverview } from '~/types/silo/models'
import type {
  TEntityId,
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItemWithOptionalAvatar,
  TEntityName,
} from '~/types/models'
import type {
  IMSService,
  TMSService,
  TMSServiceEntityName,
} from '~/types/msService/models'
import type { TMasterService } from '~/types/masterService/models'
import type {
  TMSServiceWithOmits,
  IInputMSService,
} from '~/types/msService/models'
import { assertIsDefined, assertIsNumber } from '~/types/models'
import {
  isIOpportunityOverviewData,
  isIOrderOverviewData,
  isICompanyOverviewData,
} from '~/types/silo/models'
import { propagateCMSServicesData } from './CompanyMSService'
import { ProductLevelIds } from '~/services/selectLists/productLevels'
import { isKitProduct, isItemProduct } from '~/types/product/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { readKeyIdentifierValueFromFieldSetData } from '~/cynqueModules/fieldSetKeyIdentifier'
import { peekKeyIdentifierFieldName } from '~/cynqueModules/fieldSetKeyIdentifierCache'
import * as KitQuantity from '~/cynqueModules/dataObjects/KitQuantity'
import * as KitService from '~/cynqueModules/dataObjects/KitService'
import { useModalDialogStore } from '~/stores/modalDialog'

const mn = '[cynqueModules/dataObjects/MSService.ts]'

// log helper functions

function _logGetMSQuantity(message: string, arg?: any) {
  if (
    getPublicRuntimeConfig().doSuppressGetMSQuantityBasedOnKitQuantityLogOutput
  ) {
    return
  }
  console.debug(message, arg || '')
}
function _logPreprocessing(message: string, arg?: any) {
  if (getPublicRuntimeConfig().doSuppressPreprocessingMSSDataBeforeRender) {
    return
  }
  console.debug(message, arg || '')
}

// Data Object

export function MSService(
  msService: TMSService,
  allMSServices: TMSService[],
  msServicesById?: Map<TEntityId, TMSService>
) {
  const fn = `[${mn} MSService |`
  _logPreprocessing(`${fn} Preprocessing msService's data before render`)
  msService.originAndProductLevelDescr = TypeAndProductLevelDescr(msService)
  msService.supplierAndTsdDescr = SupplierAndTsdDescr({
    supplier: msService.supplier,
    tsd: msService.tsd
      ? { id: msService.tsd.id, label: msService.tsd.descr }
      : undefined,
  })
  _logPreprocessing('', msService)
  _logGetMSQuantity(
    `${fn} qty: ${msService.quantity}, umrc: ${msService.unitRcAmount},` +
      ` unrc ${msService.unitNrcAmount}`
  )

  // calculate financial values
  msService.quantity = deriveMSServiceQuantity({
    msService,
    allMSServices,
    msServicesById,
  })
  assertIsNumber(msService.quantity) // guard
  msService.mrcAmount =
    msService.quantity *
    getRecursMrcAmount({
      unitRcAmount: msService.unitRcAmount,
      recurs: msService.recurs,
      term: msService.term,
    })
  msService.nrcAmount = msService.doMultiplyNrcByQuantity
    ? msService.quantity * (msService.unitNrcAmount ?? 0)
    : 1 * (msService.unitNrcAmount ?? 0)

  // tcv does not include nrc. This is as intended.
  msService.tcvAmount = Number(msService.term)
    ? msService.mrcAmount * Number(msService.term) +
      (msService.oneTimeAmount ?? 0)
    : msService.oneTimeAmount ?? 0

  msService.recursPaidDescr = getRecursPaidDescr({
    recurs: msService.recurs,
    paid: msService.paid,
  })

  msService.label = `${msService.product.descr} - ${msService.location.label}`

  // set properties for display
  msService.contractHridAndContractNumberDescr = `${
    msService.contract?.hrid ?? '--'
  }\n${msService.contract?.contractNumber ?? '--'}`

  msService.osomsAcceptedOnDatePlusOsomsCompletedOnDateDescr = `${
    msService.osomsAcceptedOnDate ?? '--'
  }\n${msService.osomsCompletedOnDate ?? '--'}`

  setMSServiceRowLocalDisplayFields(msService)

  return msService
}

/**
 * The display fields an msService can derive from NOTHING but its own row.
 *
 * Everything here reads only properties the row already carries, which is what
 * makes it safe on a lone msService. The rest of `MSService()` -- quantity and
 * the three money fields -- cannot be, and that is the whole distinction.
 *
 * ⚠ SPLIT OUT SO A LONE msService CAN HAVE IT. `MSService()` cannot be run over
 * a single row: it calls `deriveMSServiceQuantity`, which for a kit item looks
 * its parent kit up in `allMSServices`. Hand it `[row]` and the lookup fails,
 * logs "kitMSService is falsey. This should never happen.", returns 0 -- and
 * `MSService()` then writes mrcAmount / nrcAmount / tcvAmount as 0 over the
 * values the server sent. A case embeds exactly one companyMSService with no
 * siblings anywhere, which is why `Case()` calls THIS and not `MSService()`.
 *
 * The product names a fieldSet; the fieldSet flags one of its fields as the key
 * identifier; this msService's `fieldSetData` holds that field's value. See
 * cynqueModules/fieldSetKeyIdentifier.ts for why the fieldSet is the source and
 * `product.keyIdentifierFieldName` -- which this read until v0.20.x -- is not:
 * that column is NULL for every product on the real API, has no input-spec
 * field to set it, and its mock values include field LABELS ('Model',
 * '# Seats') rather than the `name` a fieldSetData blob is keyed by.
 *
 * ⚠ ASSIGN THE KEY IDENTIFIER ONLY WHEN WE ACTUALLY DERIVED SOMETHING. Every
 * `MSService()` call site is `Object.assign(msService, MSService(msService,
 * ...))`, so an unconditional assignment would WIPE a keyIdentifierValue that
 * arrived on the wire -- which the MSW mock now sends, and which a page that has
 * not primed the fieldSet cache (Locations, the Company Operations tab) has no
 * other source for. undefined means "could not derive", never "has none".
 *
 * ⚠ `locationPlusDescrPlusKeyIdentifier` MUST be built AFTER that assignment,
 * which is the whole reason the two live in one function. Built before it, the
 * stack reads the wire value and silently misses the derived one -- and against
 * the live API, where nothing writes `key_identifier_value` yet, that means the
 * key-identifier line is always absent.
 */
export function setMSServiceRowLocalDisplayFields(msService: TMSService) {
  const derivedKeyIdentifierValue = readKeyIdentifierValueFromFieldSetData(
    msService.fieldSetData,
    peekKeyIdentifierFieldName(msService.product?.fieldSet?.id)
  )
  if (derivedKeyIdentifierValue !== undefined) {
    msService.keyIdentifierValue = derivedKeyIdentifierValue
  }
  // The three things that identify WHICH service a row is about, stacked:
  // where it is, what it is, and -- for the products that have one -- the value
  // that separates it from its identical siblings. Five POTS lines at one
  // location differ in the third line alone.
  //
  // `\n` needs no formatter: every TableCard cell is `white-space: pre-wrap`.
  //
  // ⚠ EMPTY LINES ARE DROPPED, NOT PLACEHOLDERED. A missing half gets `--` in
  // the two-line descrs above (contractHridAndContractNumberDescr and friends),
  // because there the reader must see which half is missing. Here a row with no
  // key identifier is the NORMAL case -- most products name no field at all --
  // so a `--` line would be noise on the majority of rows, and a bare '' would
  // leave a blank line behind.
  msService.locationPlusDescrPlusKeyIdentifier = [
    msService.location?.label,
    msService.descr,
    msService.keyIdentifierValue,
  ]
    .filter(Boolean)
    .join('\n')

  return msService
}

// Other financial functions

// Note: For msServices, 'quantity' is derived from 'kitQuantity' and
// will typically be 0 or null in the db. Only in the case of a root-level
// (not nested) kitService (as opposed to any kitItemServices) does
// 'quantity' have an actual manually entered value. And only in that case
// is the kitQuantity property absent.

/**
 * Build the id -> msService index that `deriveMSServiceQuantity` uses to find a
 * kit item's parent kit.
 *
 * Call this ONCE per sweep and pass the result to every `MSService()` call in
 * that sweep. The lookup it replaces is a `.find()` over the full array run per
 * msService, so a sweep over n msServices did n^2/2 comparisons; a silo
 * overview display runs three such sweeps.
 *
 * Duplicate ids: first one wins, matching the `.find()` it replaces.
 */
export function buildMSServicesById(
  allMSServices: TMSService[]
): Map<TEntityId, TMSService> {
  const msServicesById = new Map<TEntityId, TMSService>()
  for (const msService of allMSServices) {
    if (!msServicesById.has(msService.id)) {
      msServicesById.set(msService.id, msService)
    }
  }
  return msServicesById
}

export function deriveMSServiceQuantity({
  msService,
  allMSServices,
  msServicesById,
}: {
  msService: TMSService
  allMSServices: TMSService[]
  /**
   * Optional id -> msService index over `allMSServices`, built once by the
   * caller. Without it the parent-kit lookup below is a linear scan run once
   * per msService, which makes a whole `setUpMSServices` sweep O(n squared) —
   * the dominant cost of displaying a large silo overview. Build it with
   * `buildMSServicesById()` and pass it through `MSService()`.
   *
   * Optional rather than required so the MSW mock handlers and
   * pages/Locations/[id].vue keep working unchanged.
   */
  msServicesById?: Map<TEntityId, TMSService>
}) {
  const fn = `deriveMSServiceQuantity |`
  if (isKitMSServiceNotNested(msService)) {
    const value = numberOrStringAsNumber(msService.quantity)
    _logGetMSQuantity(
      `${fn} ${msService.id} is root-level kit mss.` +
        ` Returning (possibly converted to number) mss.quantity ${value} (${typeof value})`
    )
    return value
  }
  if (isStandAloneItemMSService(msService)) {
    const value = numberOrStringAsNumber(msService.kitQuantity)
    _logGetMSQuantity(
      `${fn} ${msService.id} is stand-alone item.` +
        ` Returning quantity = msService.kitQuantity ${value}`
    )
    return value
  }
  if (isKitItemMSServiceOrNestedKitMSService(msService)) {
    _logGetMSQuantity(`${fn} allMSServices:`, allMSServices)
    _logGetMSQuantity(`${fn} mss: (${msService.id})`, msService)
    const kitMSService = msServicesById
      ? msServicesById.get(msService.parentMSServiceId as TEntityId)
      : allMSServices.find(
          (kmss: TMSService) => kmss.id === msService.parentMSServiceId
        )
    // TEMPORARY TO GET BY ERROR WIHTOUT BOMBING OUT. RESTORE WHEN
    // SERVER API ERROR IS FIXED. JR.
    // if (!kitMSService) {
    //   throw new Error(`${fn} kitMSService is falsey. This should never happen.`)
    // }
    if (!kitMSService) {
      console.error(`${fn} kitMSService is falsey. This should never happen.`)
      return 0
    }
    _logGetMSQuantity(
      `${fn} kitMSService: (${kitMSService.id}) (qty: ${kitMSService.quantity})`,
      kitMSService
    )
    assertIsDefined(msService.kitQuantity, fn, 'mss.kitQuantity')
    // ⚠ A NESTED KIT'S QUANTITY IS DERIVED HERE, NOT READ. Its stored
    // `quantity` is whatever arrived on the wire until this sweep reaches it,
    // and the sweep's order is the server's -- so reading it made a kit item's
    // quantity depend on whether its header happened to be processed first.
    // The server's own value is not this one: `omss_quantity` zeroes an
    // OPTIONAL_INCLUDED row (cynque-api-server#1570), which is exactly what a
    // taken optional nested Kit's header carries. Deriving it the same way the
    // header itself will be derived makes the answer order-independent. A
    // ROOT kit's quantity is its own, stored, and still read.
    const kitMSServiceQuantity: unknown = kitMSService.parentMSServiceId
      ? deriveMSServiceQuantity({
          msService: kitMSService,
          allMSServices,
          msServicesById,
        })
      : kitMSService.quantity
    assertIsNumber(kitMSServiceQuantity, fn, 'kitMSService.quantity')
    return KitQuantity.getQuantityBasedOnKitQuantity({
      callingFn: fn,
      itemKitQuantity: msService.kitQuantity,
      kitMSServiceQuantity,
      itemDoesQualifyToBeHidden: doesQualifyToBeHidden(msService),
    })
  } else {
    // May be a kit item in a kit with 'exclude kit header' true
    if (doesQualifyToBeHidden(msService)) {
      _logGetMSQuantity(`${fn} doesQualityToBeHidden. Returning 0.`)
      return 0
    }
  }
  return Number(msService.kitQuantity) ? msService.kitQuantity : 0
}

export function buildServiceForStandAloneItem({
  item,
}: {
  item: IInputMSService
}): TMSServiceWithOmits {
  return {
    id: item.id,
    masterService: item.masterService,
    type: item.type,
    product: cloneDeep(item.product),
    parentProduct: undefined,
    parentMSServiceId: undefined,
    descr: item.product.descr || '',
    origin: item.origin,
    location: item.location,
    supplier: item.supplier,
    tsd: item.tsd,
    splitFraction: item.splitFraction ?? 0,
    term: item.term,
    recurs: item.recurs,
    paid: item.paid,
    kitQuantity: item.kitQuantity.toString(),
    quantity: null, // qty is derived. Only has a stored value for a kitService.
    unitRcAmount: item.unitRcAmount ?? 0,
    mrcAmount: item.mrcAmount ?? 0,
    unitNrcAmount: item.unitNrcAmount ?? 0,
    nrcAmount: item.nrcAmount ?? 0,
    oneTimeAmount: item.oneTimeAmount ?? 0,
    tcvAmount: 0,
    doMultiplyNrcByQuantity: item.doMultiplyNrcByQuantity,
    closeByDate: item.closeByDate,
    status: item.status,
    doInclude: item.doInclude,
    canBeDeleted: true,
  }
}

// Propagations

export function propagateMSServicesData({
  overviewData,
  allMSServices,
  setUpMSServices,
}: {
  overviewData: TSiloOverview
  allMSServices: TMSService[]
  setUpMSServices: Function
}) {
  const fn = `${mn} propagateMSServicesData |`
  console.debug(`${fn}`)
  if (isIOpportunityOverviewData(overviewData)) {
    console.debug(`${fn} allMSServices: `, allMSServices)
    propagateOMSServicesData({
      overviewData,
      allOMSServices: allMSServices,
      setUpOpportunityMSServices: setUpMSServices,
    })
  } else if (isIOrderOverviewData(overviewData)) {
    propagateMOMSServicesData({
      overviewData,
      allMOMSServices: allMSServices,
      setUpOrderMSServices: setUpMSServices,
    })
  } else if (isICompanyOverviewData(overviewData)) {
    propagateCMSServicesData({
      overviewData,
      allCMSServices: allMSServices,
      setUpCompanyMSServices: setUpMSServices,
    })
  } else {
    console.error(
      `${fn} NOT AN OPPORTUNITY-OVERVIEW, ORDER-OVERVIEW,` +
        ` OR COMPANY-OVERVIEW THIS IS A PROBLEM!!!!`
    )
  }
}

/**
 * The rows "Assign to Contract(s)" writes from a service group or a whole root
 * entity: every included, uncontracted row of the chosen supplier (kits with
 * their whole subtree) takes the chosen contract and `statusToSet`, and --
 * when the dialog's box is ticked -- every excluded row is set Closed with no
 * contract.
 *
 * `statusToSet` is per silo. The Order and Company silos still write their
 * own (Closed Won / Assigned). The Opportunity silo writes ASSIGNED_TO_CONTRACT
 * since WXTG/cynque-api-server#1816 -- signing the contract is what sets
 * Closed Won now -- and passes `isAssignableRoot` so a Bid Rejected or Closed
 * root is left alone.
 */
export async function buildUpdateArrayForAssignToContractRequest({
  msServices,
  updatedEntityData,
  calliedFromMn,
  closedStatus,
  statusToSet,
  isAssignableRoot,
}: {
  msServices: TMSService[]
  updatedEntityData: Partial<TMasterService>
  calliedFromMn: string
  closedStatus: ISelectItem
  statusToSet: ISelectItem
  /**
   * OPTIONAL. Which root kit / stand-alone rows may be assigned at all, beyond
   * having no contract. Absent, every uncontracted root qualifies -- the
   * behaviour the Order and Company silos have always had.
   */
  isAssignableRoot?: (msService: TMSService) => boolean
}): Promise<any> {
  const fn = `${calliedFromMn} --> ${mn} buildUpdateArrayForAssignToContractRequest |`
  console.debug('*'.repeat(100))
  console.debug(`${fn} passed in msServices:`, msServices)
  console.debug(`${fn} updatedEntityData:`, updatedEntityData)
  console.debug(
    `${fn} hasOwnProperty supplier:`,
    updatedEntityData.hasOwnProperty('supplier')
  )
  console.debug(
    `${fn} hasOwnProperty contract:`,
    updatedEntityData.hasOwnProperty('contract')
  )

  const rootKitAndStandaloneMSServices = msServices.filter(
    (omss) =>
      !omss.parentMSServiceId && // Root level kit or stand alone item
      !omss.contract && // Not already assigned to a contract
      (!isAssignableRoot || isAssignableRoot(omss))
  )
  console.debug(
    `${fn} rootKitAndStandaloneMSServices:`,
    rootKitAndStandaloneMSServices
  )
  const allTargetMSServices = [] as unknown as TMSService[]
  const returnArray = [] as Partial<TMSService>[]

  for (const msService of rootKitAndStandaloneMSServices) {
    switch (msService.product.level.id) {
      case ProductLevelIds.KIT: {
        const kitItemServices =
          KitService.getServicesForKitServiceFromProvidedServices({
            providedServices: msServices,
            kitService: msService,
            doIncludeKitService: true,
            doIncludeDoesQualifyToBeHidden: true,
          })
        allTargetMSServices.push(...kitItemServices)
        break
      }
      case ProductLevelIds.ITEM: {
        allTargetMSServices.push(msService)
        break
      }
      default: {
        console.debug(
          `${fn} msService.product.level.id is not KIT or ITEM id. Product:`,
          msService.product
        )
        throw new TypeError(
          `${fn} msService.product.level.id is not KIT or ITEM id.` +
            ` Should never happen.`
        )
        break
      }
    }
  }

  const includedMSServicesForSelectedSupplier = allTargetMSServices
    .filter(
      (kis) =>
        kis.doInclude && kis.supplier?.id === updatedEntityData.supplier.id
    )
    .map((kis) => ({
      id: kis.id,
      contract: updatedEntityData.contract,
      status: statusToSet,
      version: kis.version,
    }))

  const excludedMSServicesToSetClosed =
    updatedEntityData.doSetExcludedToClosedStatus
      ? allTargetMSServices
          .filter((kis) => !kis.doInclude && kis.status.id !== closedStatus.id)
          .map((kis) => ({
            id: kis.id,
            contract: null,
            status: closedStatus,
            version: kis.version,
          }))
      : []

  returnArray.push(
    ...includedMSServicesForSelectedSupplier,
    ...excludedMSServicesToSetClosed
  )

  console.debug(`${fn} returning:`, returnArray)
  console.debug('*'.repeat(100))
  return returnArray
}

export function updateKitItemQuantitiesLocally({
  callingFn,
  updatedEntityData,
  entityId,
  msServices,
  allMSServices,
}: {
  callingFn: string
  updatedEntityData: Partial<IEntityDataItem> | Partial<IEntityDataItem>[]
  entityId: TEntityId
  msServices: TMSService[]
  allMSServices: TMSService[]
}) {
  const fn = `${callingFn} --> ${mn} updateKitItemQuantitiesLocally |`
  if (!updatedEntityData.hasOwnProperty('quantity')) {
    throw new Error(
      `${fn} called with updatedEntityData lacking 'quantity' property`
    )
  }
  const updatedMSService = msServices.find(
    (msService) => msService.id === entityId
  )
  console.debug(`${fn} found updatedMSService:`, updatedMSService)
  if (!updatedMSService) {
    throw new Error(`${fn} Passed an entityId for a non-existent msService.`)
  }

  if (updatedMSService.product.level.id === ProductLevelIds.ITEM) {
    return
  }
  const kitItems = KitService.getServicesForKitServiceFromProvidedServices({
    providedServices: msServices,
    kitService: updatedMSService,
    doIncludeKitService: true,
    doIncludeDoesQualifyToBeHidden: true,
  })

  console.debug(`${fn} Kit items:`, kitItems)
  kitItems.forEach((item) => {
    item.quantity = deriveMSServiceQuantity({
      msService: item,
      allMSServices,
    })
  })
  console.debug(`${fn} Kit items:`, kitItems)
  // Note: The changed quantities are -not- being set server side, with the exception of the root kit quantity.
  // All the other quantities are calculated from the root kit quantity, so theoretically we don't need to set
  // the other values in the db. We may, in practice, need to. Not clear yet. JR.
}

export function getUniqueSuppliersFromMSServices(msServices: IMSService[]) {
  return msServices.reduce(
    (
      uniqueSuppliers: IEntitySelectItemWithOptionalAvatar[],
      mss: IMSService
    ) => {
      if (mss.supplier && mss.doInclude) {
        if (!uniqueSuppliers.some((obj) => obj.id === mss.supplier?.id)) {
          uniqueSuppliers.push(mss.supplier)
        }
      }
      return uniqueSuppliers
    },
    []
  )
}

export function isKitMSService(msService: TMSService) {
  return isKitProduct(msService.product)
}

export function isKitMSServiceNotNested(msService: TMSService) {
  return isKitMSService(msService) && !msService.parentProduct
}

export function isKitItemMSServiceOrNestedKitMSService(msService: TMSService) {
  return !!msService.parentMSServiceId
}

export function isStandAloneItemMSService(msService: TMSService) {
  return isItemProduct(msService.product) && !msService.parentProduct
}

export function isMSServiceWithProductLevelItem(msService: TMSService) {
  return (
    isItemProduct(msService.product) &&
    msService.itemAvailabilityState?.id !== ItemAvailabilityIds.NOT_AVAILABLE
  )
}

//
// set MSServiceStatus functions
//

export async function setMSServiceStatus({
  callingFn = undefined,
  item,
  onHoldStatus = undefined, // Don't have on-hold implemented for all entities yet. JR.
  closedStatus,
  closedWonStatus,
  reopenedStatus,
  statusToSetId,
  doResetCurrentlyClosedWonOrEquivalentMSSs = false,
  doResetCurrentlyClosedOrEquivalentMSSs = false,
  doClearContractProperty = false,
  providedServices,
  kitService,
  doIncludeKitService = false,
  doIncludeDoesQualifyToBeHidden = false,
  additionalPatchFields = undefined,
  getMSServiceStatusByIdFunction,
  msServiceEntityName,
  msServiceUpdated,
  rootEntityName,
  preRequestProcessor = undefined,
}: {
  callingFn?: string | undefined
  item: TMSService
  onHoldStatus?: ISelectItem | undefined
  closedStatus: ISelectItem
  closedWonStatus: ISelectItem
  reopenedStatus: ISelectItem
  statusToSetId: number
  doResetCurrentlyClosedWonOrEquivalentMSSs?: boolean
  doResetCurrentlyClosedOrEquivalentMSSs?: boolean
  doClearContractProperty?: boolean
  additionalPatchFields?: Record<string, unknown>
  providedServices: TMSService[]
  kitService: TMSService
  doIncludeKitService: boolean
  doIncludeDoesQualifyToBeHidden: boolean
  getMSServiceStatusByIdFunction: Function
  msServiceEntityName: TMSServiceEntityName
  msServiceUpdated: Function
  rootEntityName: string
  /**
   * OPTIONAL. Turns this status change into ONE transactional write.
   *
   * Forwarded to `patchEntityData`, which runs it on the row-patch array built
   * below and then brand-detects whatever it returns: an `IEntityActionsPayload`
   * routes to `/api/transactions` instead of the plain array PATCH. Setting an order
   * service COMPLETE uses this to create the company's live services in the same
   * request — see cynqueModules/siloRelated/buildOrderCompleteCompanyActions.ts.
   *
   * ⚠ ABSENT IS THE DEFAULT AND CHANGES NOTHING. `setClosed`, `setClosedWon`,
   * `reopen` and `setPreorder` all reach here and none of them passes one, so
   * their behaviour is byte-for-byte what it was.
   *
   * 🔑 The processor receives the rows AS THEY ARE ABOUT TO BE WRITTEN — status
   * already resolved (including the doInclude:false -> CANCELLED rule), rows
   * already at the target status filtered out, versions attached. That is why
   * this seam needs no "projection" of pending state, unlike the contract-sign
   * builder, which runs before its own status change exists anywhere.
   */
  preRequestProcessor?: Function
}): Promise<(TMSService | undefined)[] | undefined> {
  const fn = `${
    callingFn ? callingFn + ' ---> ' : ''
  }${mn} setMSServiceStatus |`
  // ITEM
  if (item.product.level.id === ProductLevelIds.ITEM) {
    console.debug(`${fn} Setting for an ITEM`)
    const updatedEntityData = await _setMSServiceStatus({
      msServices: [item],
      onHoldStatus,
      closedStatus,
      closedWonStatus,
      reopenedStatus,
      statusToSetId,
      doResetCurrentlyClosedWonOrEquivalentMSSs,
      doResetCurrentlyClosedOrEquivalentMSSs,
      doClearContractProperty,
      additionalPatchFields,
      errorEntityDescr: `${rootEntityName} Service`,
      getMSServiceStatusByIdFunction,
      msServiceEntityName,
      msServiceUpdated,
      rootEntityName,
      preRequestProcessor,
    })
    return updatedEntityData
  }
  // KIT
  console.debug(`${fn} Setting for a KIT`)
  const kitItemServices =
    KitService.getServicesForKitServiceFromProvidedServices({
      callingFn: fn,
      providedServices,
      kitService,
      doIncludeKitService,
      doIncludeDoesQualifyToBeHidden,
    })
  console.debug(
    `${fn} Kit item Services (before status change):`,
    kitItemServices
  )

  const updatedEntityData = await _setMSServiceStatus({
    msServices: kitItemServices,
    onHoldStatus,
    closedStatus,
    closedWonStatus,
    reopenedStatus,
    statusToSetId,
    doResetCurrentlyClosedWonOrEquivalentMSSs,
    doResetCurrentlyClosedOrEquivalentMSSs,
    doClearContractProperty,
    additionalPatchFields,
    errorEntityDescr: 'Kit',
    getMSServiceStatusByIdFunction,
    msServiceEntityName,
    msServiceUpdated,
    rootEntityName,
    preRequestProcessor,
  })
  return updatedEntityData
  //
}

export async function _setMSServiceStatus({
  msServices,
  onHoldStatus,
  closedStatus,
  closedWonStatus,
  reopenedStatus,
  statusToSetId,
  doResetCurrentlyClosedWonOrEquivalentMSSs = false,
  doResetCurrentlyClosedOrEquivalentMSSs = false,
  doClearContractProperty = false,
  additionalPatchFields = undefined,
  errorEntityDescr,
  getMSServiceStatusByIdFunction,
  msServiceEntityName,
  msServiceUpdated,
  rootEntityName,
  preRequestProcessor,
}: {
  msServices: TMSService[]
  onHoldStatus?: ISelectItem | undefined
  closedStatus: ISelectItem
  closedWonStatus: ISelectItem
  reopenedStatus: ISelectItem
  statusToSetId: number
  doResetCurrentlyClosedWonOrEquivalentMSSs?: boolean
  doResetCurrentlyClosedOrEquivalentMSSs?: boolean
  doClearContractProperty?: boolean
  additionalPatchFields?: Record<string, unknown>
  errorEntityDescr: string
  getMSServiceStatusByIdFunction: Function
  msServiceEntityName: TMSServiceEntityName
  msServiceUpdated: Function
  rootEntityName?: TEntityName
  /** See the same parameter on `setMSServiceStatus` above. */
  preRequestProcessor?: Function
}): Promise<(TMSService | undefined)[] | undefined> {
  //
  function _getStatusToUse(
    mss: TMSService,
    closedWonStatus: ISelectItem,
    statusToSetId: number
  ): ISelectItem {
    const statusToUse =
      !mss.doInclude && statusToSetId === closedWonStatus.id
        ? closedStatus
        : getMSServiceStatusByIdFunction(statusToSetId)
    console.debug(`${fn} statusToUse is`, statusToUse)
    return statusToUse
  }
  //
  function _doClearContractProperty(
    statusToSetId: number,
    doClearContractProperty: boolean
  ) {
    const doClear =
      statusToSetId === reopenedStatus.id && doClearContractProperty
    console.debug(`${fn} _doClearContractProperty returning`, doClear)
    return doClear
  }
  //
  // Execution starts here
  //
  const fn = `${mn} _setMSServiceStatus |`
  console.debug(`${fn} Setting (for ${errorEntityDescr})...`)
  console.debug(`${fn}   onHoldStatus:`, onHoldStatus)
  console.debug(`${fn}   closedStatus:`, closedStatus)
  console.debug(`${fn}   closedWonStatus:`, closedWonStatus)
  console.debug(`${fn}   reopenedStatus:`, reopenedStatus)
  console.debug(`${fn}`)
  console.debug(`${fn}   statusToSetId:`, statusToSetId)
  console.debug(`${fn}   doClearContractProperty:`, doClearContractProperty)
  const updatedEntityData = msServices
    .filter((mss) =>
      statusToSetId === closedStatus.id || statusToSetId === closedWonStatus.id
        ? ((doResetCurrentlyClosedOrEquivalentMSSs &&
            mss.status.id === closedStatus.id) ||
            (!doResetCurrentlyClosedOrEquivalentMSSs &&
              mss.status.id !== closedStatus.id)) &&
          ((doResetCurrentlyClosedWonOrEquivalentMSSs &&
            mss.status.id === closedWonStatus.id) ||
            mss.status.id !== closedWonStatus.id)
        : true
    )
    .map((mss) => ({
      id: mss.id,
      status: _getStatusToUse(mss, closedWonStatus, statusToSetId),
      ...(_doClearContractProperty(statusToSetId, doClearContractProperty) && {
        contract: null, // Value will get stringified. Can't use undefined.
      }),
      ...additionalPatchFields,
      version: mss.version,
    })) as Partial<TMSService[]>

  console.debug(`${fn} updatedEntityData:`, updatedEntityData)

  try {
    await useModalDialogStore().setIsProcessing(true)
    // await msServiceEntityName(updatedEntityData) // API call
    // ⚠ THE ARRAY GOES IN, NOT THE PAYLOAD. `patchEntityData` validates
    // `entityId` against array-ness BEFORE running the processor, and this call
    // site passes `entityId: ''` — handing it an object up front throws
    // "updatedEntityData is -not- an array, and entityId is falsey". Passing the
    // processor instead lets it swap in an actions payload after that check,
    // which `patchEntityData` then brand-detects and routes to /api/transactions.
    await patchEntityData({
      entityName: msServiceEntityName,
      updatedEntityData,
      entityId: '',
      preRequestProcessor,
      afterRequestCallback: (args: any) => {
        useModalDialogStore().clearOnOkCallbackData()
        msServiceUpdated(args)
      },
    })
    console.debug(`${fn} calling msServiceUpdated`)
    // msServiceUpdated({
    //   // local data update
    //   updatedEntityData,
    // })
    return updatedEntityData
  } catch (error) {
    console.error(`${fn}`, error)
    await useModalDialogStore().clearOnOkCallbackData()
    addNotification({
      type: 'warning',
      message: `There was a problem setting this ${errorEntityDescr} to ${
        getMSServiceStatusByIdFunction(statusToSetId).label
      }. Please try again later.`,
      statusCode: undefined, // Should pick up statusCode from error, but don't want to deal with type issues now.  JR.
    })
  } finally {
    await useModalDialogStore().clearIsProcessing()
    await useModalDialogStore().clearModalDialog()
  }
}
